import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMenu,
  createItem,
  updateItem,
  getModifierGroups,
  getInventoryItems,
  getInventorySettings,
  getOrgRecipes,
} from '../services/api';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import ItemForm from '../components/ItemForm';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { trackEvent } from '../utils/analytics';
import { storage } from '../utils/storage';
import {
  prepareItemForEdit,
  transformItemForSave,
  normalizeModifierGroupIds,
  sameIdSet,
  sameRecipe,
  sameInventoryLink,
} from '../utils/itemForm';
import './ItemFormPage.css';

function ItemFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId } = useParams();
  const { t } = useTranslation();
  const isEditMode = !!itemId;
  const { setDynamicLabel } = useBreadcrumb();

  const [menu, setMenu] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemData, setItemData] = useState(location.state?.item || null);
  const [categoryId] = useState(location.state?.categoryId || '');
  const [isDirty, setIsDirty] = useState(false);
  // RES-45: candado de recetas + recetas estándar de la organización (origen de la receta)
  const [recipeAccess, setRecipeAccess] = useState(null);
  const [orgRecipes, setOrgRecipes] = useState(null);
  const [saveError, setSaveError] = useState('');
  const { showModal, confirmNavigation, cancelNavigation } = useUnsavedChanges(isDirty);

  const handleDirtyChange = useCallback((dirty) => {
    setIsDirty(dirty);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode && itemData?.name) {
      setDynamicLabel(itemData.name);
    }
  }, [isEditMode, itemData, setDynamicLabel]);

  const loadData = async () => {
    try {
      const response = await getMenu();
      const activeMenu = (response.menu || [])
        .filter((cat) => cat.status !== 'deleted')
        .map((cat) => ({
          ...cat,
          items: (cat.items || []).filter((item) => item.status !== 'deleted'),
        }));
      setMenu(activeMenu);

      const restaurantId = storage.get('selectedRestaurantId');
      if (restaurantId) {
        const modData = await getModifierGroups(restaurantId);
        setModifierGroups(modData.modifierGroups || []);

        const invData = await getInventoryItems();
        setInventoryItems(invData.items || []);
      }

      // Solo al editar: el origen de la receta es de un producto existente. Si fallan (plan sin
      // inventario, sin permiso) el formulario sigue como antes y el backend decide al guardar.
      if (isEditMode) {
        const [settings, recipes] = await Promise.all([
          getInventorySettings().catch(() => null),
          getOrgRecipes().catch(() => null),
        ]);
        setRecipeAccess(settings?.can || null);
        setOrgRecipes(Array.isArray(recipes?.recipes) ? recipes.recipes : null);
      }

      // If edit mode and no state passed, find item from loaded menu
      if (isEditMode && !location.state?.item) {
        for (const cat of activeMenu) {
          const found = (cat.items || []).find((i) => i.itemId === itemId);
          if (found) {
            setItemData(prepareItemForEdit(found, cat.categoryId));
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    setSaveError('');
    try {
      await createItem(transformItemForSave(formData));
      trackEvent('menu_item_created', {
        restaurant_id: storage.get('selectedRestaurantId'),
        organization_id: storage.get('selectedOrganizationId'),
        has_image: !!formData.imageUrl,
        has_modifiers: (formData.modifierGroups?.length || 0) > 0,
        category_id: formData.categoryId || categoryId,
      });
      setIsDirty(false);
      navigate('/menu');
    } catch (error) {
      setSaveError(inventoryErrorMessage(t, error, 'menu.createItemError'));
    }
  };

  const handleUpdate = async (formData) => {
    setSaveError('');
    try {
      const payload = transformItemForSave(formData);
      // Si no se tocó la sección de modificadores, no se reenvía: el backend
      // conserva lo guardado y una carga incompleta no puede borrarlos (QA R3).
      const initialIds = normalizeModifierGroupIds(itemData?.modifierGroups);
      if (sameIdSet(initialIds, payload.modifierGroups)) {
        delete payload.modifierGroups;
      }
      // Idem receta (RES-48): getMenu solo la devuelve en contexto admin; si no se tocó, no se
      // reenvía y el backend conserva la guardada.
      if (sameRecipe(itemData?.recipe, payload.recipe)) {
        delete payload.recipe;
      }
      // Idem vínculo de reventa (RES-29).
      if (sameInventoryLink(itemData?.inventoryLink, payload.inventoryLink)) {
        delete payload.inventoryLink;
      }
      await updateItem(itemId, payload);
      setIsDirty(false);
      navigate('/menu');
    } catch (error) {
      // RES-45: RECIPES_LOCKED (la organización bloqueó las recetas), ORG_RECIPE_NOT_FOUND, etc.
      setSaveError(inventoryErrorMessage(t, error, 'menu.updateItemError'));
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="item-form-page">
      <div className="form-header">
        <h1>{isEditMode ? t('menu.editItem') : t('menu.newItem')}</h1>
        <p>{isEditMode ? t('menu.itemForm.editSubtitle') : t('menu.itemForm.newSubtitle')}</p>
      </div>

      <ItemForm
        initialData={isEditMode ? itemData : categoryId ? { categoryId } : undefined}
        categories={menu}
        modifierGroups={modifierGroups}
        inventoryItems={inventoryItems}
        onSubmit={isEditMode ? handleUpdate : handleCreate}
        onCancel={() => navigate('/menu')}
        onDirtyChange={handleDirtyChange}
        submitLabel={isEditMode ? t('common.save') : t('menu.createItem')}
        draftKey={isEditMode ? itemId : 'new'}
        focusSection={location.state?.focusSection}
        recipeAccess={recipeAccess}
        orgRecipes={orgRecipes}
        submitError={saveError}
      />

      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-changes-title"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="unsaved-changes-title">{t('common.unsavedChangesTitle')}</h2>
            </div>
            <div className="modal-body">
              <p>{t('common.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelNavigation}>
                {t('common.unsavedChangesStay')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmNavigation}>
                {t('common.unsavedChangesLeave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemFormPage;
